import React from "react";
import { useNavigate } from "react-router-dom";
export default function RefundPolicy() {
  const pageWrapperStyle = {
    minHeight: "100vh",
    padding: "7rem 0.75rem",
    backgroundColor: "#f9fafb", // Light background
    fontFamily: "'Inter', sans-serif",
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    maxWidth: "1024px",
    margin: "0 auto",
    padding: "2.5rem",
    borderRadius: "1rem",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 10px rgba(0, 0, 0, 0.05)",
  };

  const titleStyle = {
    fontWeight: "800",
    fontSize: "2.25rem",
    textAlign: "center",
    marginBottom: "2.5rem",
    color: "#991b1b", // Green for Policy/Acceptance theme
  };

  const sectionHeadingStyle = {
    fontSize: "1.5rem",
    fontWeight: "700",
    marginTop: "2rem",
    marginBottom: "1rem",
    color: "#1f2937",
    borderBottom: "2px solid #991b1b", // Green accent
    paddingBottom: "0.5rem",
  };

  const subHeadingStyle = {
    fontSize: "1.125rem", // text-lg
    fontWeight: "600",
    marginTop: "1.5rem",
    marginBottom: "0.75rem",
    color: "#1f2937",
  };

  const textStyle = {
    fontSize: "1rem",
    lineHeight: "1.625",
    color: "#4b5563",
    marginBottom: "1.5rem",
  };

  const listStyle = {
    listStyleType: 'disc',
    paddingLeft: '1.25rem',
  };

  const listItemStyle = {
    marginBottom: '0.5rem',
  };

  const warningBoxStyle = {
    backgroundColor: "#f0fdf4", // Light Green/Success Box
    borderLeft: "4px solid #991b1b", // Emerald accent
    color: "#991b1b",
    padding: "1rem",
    borderRadius: "0.5rem",
    marginTop: "1rem",
  };

  const infoBoxStyle = {
    backgroundColor: "#fef2f2",
    borderLeft: "4px solid #ef4444",
    color: "#991b1b",
    padding: "1rem",
    borderRadius: "0.5rem",
    marginTop: "0.75rem",
  };

  const linkStyle = {
    color: "#991b1b",
    textDecoration: "none",
    fontWeight: "600",
  };
  
  const buttonContainerStyle = {
    textAlign: "center",
    marginTop: "2.5rem",
  };

  const buttonStyle = {
    backgroundColor: "#991b1b",
    color: "white",
    fontWeight: "600",
    padding: "0.75rem 2rem",
    borderRadius: "9999px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
    border: 'none',
    cursor: 'pointer',
  };

  const navigate = useNavigate();

  return (
    <div style={pageWrapperStyle}>
      <div style={cardStyle}>
        <h2 style={titleStyle}>
           Refund & Store Credit Policy
        </h2>

        {/* Section 1: Core Policy and Exchange Window */}
        <div>
          <h3 style={sectionHeadingStyle}>
            1. Core Return / Refund Policy
          </h3>
          <p style={textStyle}>
            Customer satisfaction is our utmost priority. However, please note our core policy regarding refunds and returns:
          </p>

          <p style={infoBoxStyle}>
            We DO NOT accept returns or cash refunds.
            <br />
            For eligible issues, we offer exchange with other products or store credit only. We do not accept cash refund requests on any products sold at any time.
          </p>
          
          <h4 style={subHeadingStyle}>Eligibility Window:</h4>
          <p style={textStyle}>
            If you are not satisfied with what you received, replacement/exchange will be accepted within 7 days from the date of delivery.
          </p>
          <p style={{...textStyle, marginTop: '-0.5rem'}}>
            <a href="ExchangeReturnPolicy.jsx" style={linkStyle}>Kindly go through our Exchange & Return Policy</a> to understand more about the detailed exchange procedure.
          </p>
        </div>

        {/* Section 2: Item Eligibility for Exchange */}
        <div>
          <h3 style={sectionHeadingStyle}>
            2. Items Eligible for Exchange / Store Credit
          </h3>
          <div style={textStyle}>
            <p>An item is eligible for exchange or store credit if it meets the following criteria:</p>
            <ul style={listStyle}>
              <li style={listItemStyle}>The dissatisfaction is due to manufacturing defects or wrong product/size sent by us.</li>
              <li style={listItemStyle}>An Unpacking Video is a must for all exchange claims.</li>
              <li style={listItemStyle}>The item you are returning must be unwashed and unused, with original clothing tags attached.</li>
              <li style={listItemStyle}>Replacements/Store Credit will be processed only once the original items are received, pass the quality checks, and are approved for exchange.</li>
            </ul>
          </div>
        </div>

        {/* Section 3: Damaged, Incorrect, and Non-Returnable Items */}
        <div>
          <h3 style={sectionHeadingStyle}>
            3. Damaged / Incorrect Items and Exceptions
          </h3>
          <div style={textStyle}>
            <ul style={listStyle}>
              <li style={listItemStyle}>
                Damaged or Incorrect Items: If you receive a damaged or incorrect item, please reach out to us within 24 hours of delivery for assistance. We will arrange for a replacement or store credit based on availability.
              </li>
              <li style={listItemStyle}>
                Non-Returnable Items: Items marked as "Final Sale" or those purchased during promotional events may not be eligible for exchanges. Please check product details before purchasing.
              </li>
              <li style={listItemStyle}>
                No Direct Exchanges: We currently do not offer direct exchanges. Please initiate the return/exchange process as outlined in the <a href="ExchangeReturnPolicy.jsx" style={linkStyle}>Exchange & Return Policy</a> and place a new order using your Store Credit for the item you want.
              </li>
            </ul>
          </div>
        </div>
        
        {/* Section 4: Refund Processing (Store Credit Only) */}
        <div>
          <h3 style={sectionHeadingStyle}>
            4. Store Credit Issuance (Equivalent to Refund)
          </h3>
          <div style={textStyle}>
            <p style={warningBoxStyle}>
              Upon receiving and inspecting the returned item, we will notify you of the status of your claim. Approved claims will be processed as Store Credit to your account/email.
            </p>
            <ul style={listStyle}>
              <li style={listItemStyle}>Approved Store Credit will be issued within 5 to 7 business days after the item passes the quality check.</li>
              <li style={listItemStyle}>Shipping charges (if any) are non-refundable, except when the item received was verified as damaged or incorrect by our team.</li>
            </ul>
          </div>
        </div>

        <div style={buttonContainerStyle}>
          <button
            style={buttonStyle}
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}